import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { CLOSED_ADMISSION_STATUS, DUES_AGE_BUCKETS, type DuesRow } from '@his/shared';
import { get, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly, downloadCsv, humanize, money, todayIso } from '@/lib/utils';
import { DataTable, FilterField, PageHeader, SearchFilterBar, StatTile } from '@/components/ui/layout';
import { Badge, Button, Select, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface Summary {
  totalAdmissions: number;
  totalDueMinor: number;
  advanceHeldAdmissions: number;
  advanceHeldMinor: number;
  buckets: { bucket: string; admissions: number; dueMinor: number }[];
}

const WINDOWS = [
  { days: 90, label: 'Last 3 months' },
  { days: 180, label: 'Last 6 months' },
  { days: 365, label: 'Last year' },
  { days: 730, label: 'Last 2 years' },
];

export function DuesRegisterPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(365);
  const [status, setStatus] = useState('');
  const [bucket, setBucket] = useState('');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const toast = useToast();

  const summary = useQuery({ queryKey: ['dues-summary', days], queryFn: () => get<Summary>(`/ipd/dues/summary${qs({ days })}`) });

  const register = useInfiniteQuery({
    queryKey: ['dues', days, status, bucket, search],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      get<{ data: DuesRow[]; nextCursor: string | null }>(
        `/ipd/dues${qs({ days, status: status || undefined, bucket: bucket || undefined, q: search || undefined, limit: 50, cursor: pageParam })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = register.data?.pages.flatMap((page) => page.data);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const all = await get<DuesRow[]>(`/ipd/dues/export${qs({ days, status: status || undefined, bucket: bucket || undefined, q: search || undefined })}`);
      downloadCsv(`dues-register-${todayIso()}`, all, [
        { header: tr('ipd', 'admission'), value: (row) => row.number },
        { header: tr('ipd', 'mrn'), value: (row) => row.patient.mrn },
        { header: tr('ipd', 'patient'), value: (row) => row.patient.fullName },
        { header: tr('ipd', 'phone'), value: (row) => row.patient.phone },
        { header: tr('ipd', 'outcome'), value: (row) => row.status },
        { header: tr('ipd', 'closed_on'), value: (row) => (row.dischargedAt ? row.dischargedAt.slice(0, 10) : '') },
        { header: tr('ipd', 'age_days'), value: (row) => row.ageDays },
        { header: tr('ipd', 'age_bucket'), value: (row) => row.ageBucket },
        { header: tr('ipd', 'billed'), value: (row) => row.billedMinor / 100 },
        { header: tr('ipd', 'paid'), value: (row) => row.paidMinor / 100 },
        { header: tr('ipd', 'advance_held'), value: (row) => row.advanceMinor / 100 },
        { header: tr('ipd', 'owing'), value: (row) => row.dueMinor / 100 },
        { header: tr('ipd', 'advance_kept_after_absconding'), value: (row) => row.advanceHeldMinor / 100 },
        { header: tr('ipd', 'last_follow_up'), value: (row) => row.lastNote?.note ?? '' },
        { header: tr('ipd', 'promised_date'), value: (row) => row.lastNote?.promisedDate ?? '' },
      ]);
    } catch (error) {
      toast.error(tr('ipd', 'the_register_could_not_be_exported'), describeError(error));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageHeader
        title={tr('ipd', 'dues_register')}
        description={tr('ipd', 'patients_who_have_left_and_still_owe_money_with')}
      />

      {summary.data && (
        <div className="mb-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <StatTile label={tr('ipd', 'owed_in_total')} value={money(summary.data.totalDueMinor)} sub={tr('ipd', 'x_admission_s', { totalAdmissions: summary.data.totalAdmissions })} tone={summary.data.totalDueMinor > 0 ? 'warning' : 'default'} />
          {summary.data.buckets.map((entry) => (
            <StatTile
              key={entry.bucket}
              label={tr('ipd', 'x_days', { bucket: entry.bucket })}
              value={money(entry.dueMinor)}
              sub={tr('ipd', 'x_admission_s_2', { admissions: entry.admissions })}
              tone={entry.bucket === '90+' && entry.dueMinor > 0 ? 'destructive' : 'default'}
            />
          ))}
          <StatTile
            label={tr('ipd', 'advances_kept')}
            value={money(summary.data.advanceHeldMinor)}
            sub={tr('ipd', 'x_patient_s_left_not_refunded', { advanceHeldAdmissions: summary.data.advanceHeldAdmissions })}
            tone={summary.data.advanceHeldMinor > 0 ? 'warning' : 'default'}
          />
        </div>
      )}

      <SearchFilterBar
        search={{ value: search, onChange: setSearch, placeholder: tr('ipd', 'search_name_mrn_phone_or_admission') }}
        filters={
          <>
            <FilterField label={tr('ipd', 'period')}>
              <Select value={String(days)} onChange={(e) => setDays(Number(e.target.value))} className="w-44">
                {WINDOWS.map((window) => (
                  <option key={window.days} value={window.days}>
                    {window.label}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField label={tr('ipd', 'outcome')}>
              <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-44">
                <option value="">{tr('ipd', 'every_outcome')}</option>
                {CLOSED_ADMISSION_STATUS.map((value) => (
                  <option key={value} value={value}>
                    {humanize(value)}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField label={tr('ipd', 'age_bucket')}>
              <Select value={bucket} onChange={(e) => setBucket(e.target.value)} className="w-40">
                <option value="">{tr('ipd', 'any_age')}</option>
                {DUES_AGE_BUCKETS.map((value) => (
                  <option key={value} value={value}>
                    {value} {tr('ipd', 'days')}
                  </option>
                ))}
              </Select>
            </FilterField>
          </>
        }
        trailing={
          <Button variant="outline" onClick={exportCsv} loading={exporting}>
            {tr('ipd', 'export_to_spreadsheet')}
          </Button>
        }
        onReset={
          days !== 365 || status || bucket || search
            ? () => {
                setDays(365);
                setStatus('');
                setBucket('');
                setSearch('');
              }
            : undefined
        }
      />

      <DataTable
        rows={rows}
        loading={register.isLoading} refreshing={register.isFetching}
        partial={Boolean(register.hasNextPage)}
        error={register.error}
        rowKey={(row) => row.admissionId}
        empty={tr('ipd', 'nobody_who_has_left_owes_anything_and_no')}
        columns={[
          {
            header: tr('ipd', 'patient'),
            sort: (row) => row.patient.fullName,
            cell: (row) => (
              <div>
                <p className="font-medium">{row.patient.fullName}</p>
                <p className="text-xs text-muted-foreground">
                  {row.patient.mrn} · {row.patient.phone ?? tr('ipd', 'no_phone')} · {row.number}
                </p>
              </div>
            ),
          },
          {
            header: tr('ipd', 'left'),
            sort: (row) => row.dischargedAt,
            cell: (row) => (
              <div className="flex flex-wrap items-center gap-1">
                <span className="tabular">{dateOnly(row.dischargedAt)}</span>
                <StatusBadge status={row.status} />
              </div>
            ),
          },
          {
            header: tr('ipd', 'age'),
            sort: (row) => row.ageDays,
            cell: (row) => (
              <Badge variant={row.ageBucket === '90+' ? 'destructive' : row.ageBucket === '61-90' ? 'warning' : 'muted'}>{row.ageDays} d</Badge>
            ),
          },
          {
            header: tr('ipd', 'owing'),
            align: 'right',
            sort: (row) => row.dueMinor,
            cell: (row) => (
              <div>
                {row.dueMinor > 0 ? <p className="font-semibold text-destructive">{money(row.dueMinor)}</p> : <p className="text-muted-foreground">—</p>}
                <p className="text-xs text-muted-foreground">{tr('ipd', 'of')} {money(row.billedMinor)}</p>
              </div>
            ),
          },
          {
            header: tr('ipd', 'advance_kept'),
            align: 'right',
            sort: (row) => row.advanceHeldMinor,
            cell: (row) => (row.advanceHeldMinor > 0 ? <Badge variant="warning">{money(row.advanceHeldMinor)}</Badge> : '—'),
          },
          {
            header: tr('ipd', 'follow_up'),
            cell: (row) =>
              row.lastNote ? (
                <div className="max-w-xs">
                  <p className="truncate text-sm">{row.lastNote.note}</p>
                  {row.lastNote.promisedDate && (
                    <p className={`text-xs ${row.lastNote.promisedDate < todayIso() ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {tr('ipd', 'promised')} {dateOnly(row.lastNote.promisedDate)}
                      {row.lastNote.promisedAmountMinor ? ` · ${money(row.lastNote.promisedAmountMinor)}` : ''}
                    </p>
                  )}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">{tr('ipd', 'nobody_has_followed_up')}</span>
              ),
          },
          {
            header: '',
            align: 'right',
            cell: (row) => (
              <div className="flex justify-end gap-1">
                <Button size="sm" variant="outline" onClick={() => navigate(`/ipd/dues/${row.admissionId}/follow-up`)}>
                  {can('dues.manage') ? tr('ipd', 'follow_up_2') : tr('ipd', 'notes')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => navigate(`/ipd/admissions/${row.admissionId}`)}>
                  {tr('ipd', 'bill_2')}
                </Button>
              </div>
            ),
          },
        ]}
        footer={
          register.hasNextPage && (
            <Button variant="outline" size="sm" onClick={() => void register.fetchNextPage()} loading={register.isFetchingNextPage}>
              {tr('ipd', 'show_more')}
            </Button>
          )
        }
      />
    </>
  );
}
